import React, { useContext, useState } from 'react'

import { useTranslation } from 'react-i18next'

import Button from '@components/shared/Button'
import Dialog from '@components/shared/Dialog'
import Checkbox from '@components/shared/form/Checkbox'
import Input from '@components/shared/form/Input'
import { TitleContext } from '@providers/TitleProvider'
import { useDialogStore } from '@stores/dialog.store'

export const DEFAULT_HEIGHT = 800
export const DEFAULT_WIDTH = 525
export const DEFAULT_SCALE = 1

export const generateIframeText = (
  url: string | undefined,
  width: number,
  height: number,
  scale: number,
  hideCursor: boolean
): string => {
  return `<iframe allow="autoplay" allowfullscreen width="${width}" height="${height}" src="${url}?scale=${scale}&hidePlaybackCursor=${hideCursor}"></iframe>`
}

const EmbedTitleDialog: React.FC = () => {
  const { t } = useTranslation()
  const closeDialog = useDialogStore((s) => s.closeDialog)

  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [height, setHeight] = useState(DEFAULT_HEIGHT)
  const [scale, setScale] = useState(DEFAULT_SCALE)
  const [hideCursor, setHideCursor] = useState(false)

  const { title } = useContext(TitleContext)

  const iframeText = generateIframeText(title.external_embed_url, width, height, scale, hideCursor)

  const selectText = (e) => e.target.select()

  return (
    <Dialog name="embed_title" title={t('action.embedScore')} maxWidth={450}>
      <p className="mb-1">{t('content.embedInstructions')}</p>
      <Input
        multiline
        readOnly
        name="iframeText"
        value={iframeText}
        onClick={selectText}
        inputClassName="resize-none"
        rows={4}
      />
      <div className="mt-1 flex flex-row justify-start gap-3">
        <DimensionSetting name="height" value={height} setter={setHeight} />
        <DimensionSetting name="width" value={width} setter={setWidth} />
        <DimensionSetting name="scale" value={scale} setter={setScale} />
        <Checkbox
          name="cursor"
          label={t('label.hideCursor')}
          checked={hideCursor}
          onChecked={setHideCursor}
          className="mt-1"
        />
      </div>
      <div className="mt-[1rem] flex flex-row justify-end gap-1">
        <Button variant="secondary" onClick={() => closeDialog('embed_title')}>
          {t('action.cancel')}
        </Button>
      </div>
    </Dialog>
  )
}

interface SettingProps {
  name: string
  value: number
  setter: (n: number) => void
}

export const DimensionSetting: React.FC<SettingProps> = ({ name, value, setter }) => {
  const { t } = useTranslation()

  return (
    <Input
      type="number"
      min={1}
      value={value}
      onChange={(e) => setter(Number(e.target.value) || 1)}
      name={name}
      label={t(`label.${name}`)}
    />
  )
}

export default EmbedTitleDialog
